import { Bars3Icon, ChartBarIcon, RectangleStackIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getTokens, useAuthState } from '../../contexts/auth';
import { getUser, storeUser } from '../../utils/tokens';
import { find, forEach, propEq } from 'rambda';
import { Dialog, Disclosure, Transition } from '@headlessui/react';
import React, { Fragment, useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import axios from 'axios';
import { AlignAdminRoutes } from '../Routes/AlignAdminRoutes';

const navigation = [
	{
		name: 'Categories',
		href: 'categories',
		icon: ChartBarIcon,
		current: true,
	},
	{
		name: 'Resources',
		href: 'resources',
		icon: RectangleStackIcon,
		current: false,
	},
];

function classNames(...classes) {
	return classes.filter(Boolean).join(' ');
}

const listUrl = clientId => `resource/v1/resource-categories/clients/${clientId}`;

const AlignAdminHome = () => {
	let navigate = useNavigate();
	const [queryParameters] = useSearchParams();
	const { isAuthenticated } = useAuthState();

	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [categories, setCategories] = useState(null);
	const [loaded, setLoaded] = useState(false);
	const [authTransferred, setAuthTransferred] = useState(false);
	const [authKey, setAuthKey] = useState(null);
	const [currentUser, setCurrentUser] = useState(null);

	useEffect(() => {
		setAuthKey(queryParameters.get('authKey'));
		setAuthTransferred(true);
	}, []);

	useEffect(() => {
		// get and set token
		if (authKey) {
			getTokens(authKey).then(response => {
				setCurrentUser(getUser());
			});
		}
	}, [authTransferred]);

	useEffect(() => {
		setCurrentUser(getUser());
	}, [isAuthenticated]);

	useEffect(() => {
		if (currentUser) {
			const getCategories = async () => {
				return await axios
					.get(listUrl(currentUser.clientIdNum))
					.then(response => {
						setCategories(response.data);
					})
					.catch(error => {
						console.log(error);
					});
			};
			getCategories().then();
		}
	}, [currentUser]);

	useEffect(() => {
		if (categories) {
			// categories.forEach(c => {
			// 	navigation[0].children.push({ id: c.id, name: c.title, href: '#' });
			// });

			setLoaded(true);
		}
	}, [categories]);

	if (!loaded) {
		return <div></div>;
	}

	function selectNavBody(nav) {
		if (isAuthenticated) {
			const setCurrent = i => (i.current = false);
			forEach(setCurrent, navigation);
			const item = find(propEq('name', nav), navigation);
			item.current = true;
			navigate(item.href);
		}
	}

	function selectSubNavBody(nav) {
		if (isAuthenticated) {
			navigate(nav);
		}
	}

	return (
		<div>
			<Transition.Root show={sidebarOpen} as={Fragment}>
				<Dialog as="div" className="relative z-40 md:hidden" onClose={setSidebarOpen}>
					<Transition.Child
						as={Fragment}
						enter="transition-opacity ease-linear duration-300"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="transition-opacity ease-linear duration-300"
						leaveFrom="opacity-100"
						leaveTo="opacity-0">
						<div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
					</Transition.Child>

					<div className="fixed inset-0 z-40 flex">
						<Transition.Child
							as={Fragment}
							enter="transition ease-in-out duration-300 transform"
							enterFrom="-translate-x-full"
							enterTo="translate-x-0"
							leave="transition ease-in-out duration-300 transform"
							leaveFrom="translate-x-0"
							leaveTo="-translate-x-full">
							<Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-white">
								<Transition.Child
									as={Fragment}
									enter="ease-in-out duration-300"
									enterFrom="opacity-0"
									enterTo="opacity-100"
									leave="ease-in-out duration-300"
									leaveFrom="opacity-100"
									leaveTo="opacity-0">
									<div className="absolute top-0 right-0 -mr-12 pt-2">
										<button
											type="button"
											className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
											onClick={() => setSidebarOpen(false)}>
											<span className="sr-only">Close sidebar</span>
											<XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
										</button>
									</div>
								</Transition.Child>
								<div className="h-0 flex-1 overflow-y-auto pt-5 pb-4">
									<nav className="mt-5 space-y-1 px-2">
										{navigation.map(item =>
											!item.children ? (
												<span
													onClick={e => {
														e.preventDefault();
														selectNavBody(item.name);
													}}
													key={item.id}
													className={classNames(
														item.current
															? 'bg-gray-100 text-gray-900'
															: 'text-gray-800 hover:bg-gray-50 hover:text-gray-900',
														'group flex items-center px-2 py-2 text-base font-medium rounded-md',
													)}>
													<item.icon
														className={classNames(
															item.current
																? 'text-gray-500'
																: 'text-gray-400 group-hover:text-gray-500',
															'mr-4 flex-shrink-0 h-6 w-6',
														)}
														aria-hidden="true"
													/>
													{item.name}
												</span>
											) : (
												<CategoryChildren
													key={item.id}
													item={item}
													selectSubNavBody={selectSubNavBody}
												/>
											),
										)}
									</nav>
								</div>
							</Dialog.Panel>
						</Transition.Child>
						<div className="w-14 flex-shrink-0">{/* Force sidebar to shrink to fit close icon */}</div>
					</div>
				</Dialog>
			</Transition.Root>

			{/* Static sidebar for desktop */}
			<div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
				{/* Sidebar component, swap this element with another sidebar if you like */}
				<div className="flex min-h-0 flex-1 flex-col border-r border-gray-200 bg-white">
					<div className="flex flex-1 flex-col overflow-y-auto pt-5 pb-4">
						<span className="mt-5 bg-white px-2">Admin</span>
						<nav className="mt-5 flex-1 space-y-1 bg-white px-2">
							{navigation.map(item =>
								!item.children ? (
									<span
										onClick={e => {
											e.preventDefault();
											selectNavBody(item.name);
										}}
										key={item.id}
										className={classNames(
											item.current
												? 'bg-gray-100 text-gray-900'
												: 'text-gray-800 hover:bg-gray-50 hover:text-gray-900',
											'group flex items-center px-2 py-2 text-sm font-medium rounded-md hover:cursor-pointer',
										)}>
										<item.icon
											className={classNames(
												item.current
													? 'text-gray-500'
													: 'text-gray-400 group-hover:text-gray-500',
												'mr-3 flex-shrink-0 h-6 w-6',
											)}
											aria-hidden="true"
										/>
										{item.name}
									</span>
								) : (
									<CategoryChildren key={item.name} item={item} selectSubNavBody={selectSubNavBody} />
								),
							)}
						</nav>
					</div>
				</div>
			</div>
			<div className="flex flex-1 flex-col md:pl-64">
				<div className="sticky top-0 z-10 bg-white pl-1 pt-1 sm:pl-3 sm:pt-3 md:hidden">
					<button
						type="button"
						className="-ml-0.5 -mt-0.5 inline-flex h-12 w-12 items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
						onClick={() => setSidebarOpen(true)}>
						<span className="sr-only">Open sidebar</span>
						<Bars3Icon className="h-6 w-6" aria-hidden="true" />
					</button>
				</div>
				<main className="flex-1">
					<AlignAdminRoutes />
				</main>
			</div>
			<ToastContainer hideProgressBar={true} />
		</div>
	);
};

const CategoryChildren = ({ item, selectSubNavBody }) => {
	return (
		<Disclosure as="div" key={item.name} className="space-y-1">
			{({ open }) => (
				<>
					<Disclosure.Button
						className={classNames(
							item.current
								? 'bg-gray-100 text-gray-900'
								: 'bg-white text-gray-800 hover:bg-gray-50 hover:text-gray-900',
							'group w-full flex items-center pl-2 pr-1 py-2 text-left text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500',
						)}>
						<item.icon
							className="mr-3 h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
							aria-hidden="true"
						/>
						<span className="flex-1">{item.name}</span>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 20 20"
							strokeWidth="1.5"
							stroke="currentColor"
							className={classNames(
								open ? 'text-gray-400 rotate-90' : 'text-gray-300',
								'mr-2 h-5 w-5 flex-shrink-0 transform transition-colors duration-150 ease-in-out group-hover:text-gray-400',
							)}>
							<path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
						</svg>
					</Disclosure.Button>
					<Disclosure.Panel className="space-y-1">
						{item.children.map(subItem => (
							<Disclosure.Button
								onClick={e => {
									e.preventDefault();
									selectSubNavBody(subItem.href);
								}}
								key={subItem.name}
								as="a"
								href={subItem.href}
								className="group flex w-full items-center rounded-md py-2 pl-11 pr-2 text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-900"></Disclosure.Button>
						))}
					</Disclosure.Panel>
				</>
			)}
		</Disclosure>
	);
};

export default AlignAdminHome;
